<template>
  <div>
    <TheBackLink floating />
    <TheNav />
    <TheActions />

    <!-- Each link carries a mark of its own as well as its words. On a phone
         there is no room for the words and the mark is what is left, so it has
         to say which door this is rather than only that there is one, which is
         all the trailing arrow ever said. -->
    <div class="eyebrow">
      <!-- The rail beside this holds the pieces one at a time and full size,
           which is the wrong shape for browsing all of them. Kept in here
           rather than given its own corner: the top right is the actions
           cluster's, and a fixed link placed there sat on top of it. -->
      <NuxtLink
        v-if="tab === 'images' || aiClips.length"
        :to="tab === 'images' ? '/gallery' : '/gallery?tab=videos'"
        class="eyebrow__all"
        aria-label="View all"
      >
        <svg class="eyebrow__mark" viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <rect x="3" y="3" width="7" height="7" />
          <rect x="14" y="3" width="7" height="7" />
          <rect x="14" y="14" width="7" height="7" />
          <rect x="3" y="14" width="7" height="7" />
        </svg>
        <span class="eyebrow__text">View all</span>
        <svg class="eyebrow__arrow" viewBox="0 0 24 24" width="12" height="12" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
          <line x1="5" y1="12" x2="18" y2="12" />
          <polyline points="13 7 18 12 13 17" />
        </svg>
      </NuxtLink>

      <!-- The other half of the work. Somebody looking at the renders is the
           reader most likely to want the prompts behind them, and until now
           the only way through was the navigation dock. -->
      <NuxtLink to="/library" class="eyebrow__all" aria-label="Prompts and characters">
        <svg class="eyebrow__mark" viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20" />
          <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z" />
        </svg>
        <span class="eyebrow__text">Prompts &amp; characters</span>
        <svg class="eyebrow__arrow" viewBox="0 0 24 24" width="12" height="12" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
          <line x1="5" y1="12" x2="18" y2="12" />
          <polyline points="13 7 18 12 13 17" />
        </svg>
      </NuxtLink>
    </div>

    <nav class="tabs" aria-label="Kind of work" :style="{ '--active': activeTabIndex }">
      <span class="tabs__glider" aria-hidden="true" />
      <button
        v-for="option in tabs"
        :key="option.id"
        type="button"
        class="tabs__pill"
        :class="{ 'is-active': tab === option.id }"
        :aria-pressed="tab === option.id"
        @click="tab = option.id"
      >
        {{ option.label }}
      </button>
    </nav>

    <main ref="wrapper" class="h-scroll-wrapper">
      <div ref="track" class="h-scroll-track">
        <!-- Holds the horizontal track still while its own slides advance —
             see data-hold in useHorizontalScroll. -->
        <div class="h-scroll-panel" data-hold="ai-gallery">
          <div class="explore">
            <HomeAiGallery v-if="tab === 'images'" />

            <!-- The same rail, for the moving work. Falls through to the note
                 below while there is none, so the tab is honest rather than
                 empty. -->
            <HomeAiClips v-else-if="aiClips.length" />

            <div v-else class="empty">
              <div class="empty__inner">
                <h2 class="empty__title">No videos here yet</h2>
                <p class="empty__note">
                  The moving work goes up on X and Instagram first. Have a look there
                  while this fills up.
                </p>

                <div class="empty__links">
                  <a
                    v-for="link in videoLinks"
                    :key="link.name"
                    :href="link.url"
                    target="_blank"
                    rel="noopener noreferrer"
                    class="empty__link"
                  >
                    <svg v-if="link.icon === 'x'" viewBox="0 0 24 24" width="16" height="16" fill="currentColor" aria-hidden="true">
                      <path d="M18.9 2H22l-7.1 8.1L23.2 22h-6.5l-5.1-6.7L5.8 22H2.7l7.6-8.7L1.2 2h6.6l4.6 6.1zm-1.1 18h1.7L7.3 3.8H5.5z" />
                    </svg>
                    <svg v-else viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="1.9" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                      <rect x="3" y="3" width="18" height="18" rx="5" />
                      <circle cx="12" cy="12" r="4" />
                      <path d="M17.5 6.5h.01" />
                    </svg>
                    <span>{{ link.name }}</span>
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </main>
  </div>
</template>

<script setup>
import { computed, onMounted, onBeforeUnmount, nextTick, ref } from 'vue';
import { aiClips, personalInfo } from '~/data/content';

const wrapper = ref(null);
const track = ref(null);
const { start, stop } = useHorizontalScroll();

const tabs = [
  { id: 'images', label: 'Images' },
  { id: 'videos', label: 'Videos' },
];
const route = useRoute();
const tab = ref(route.query.tab === 'videos' ? 'videos' : 'images');
const activeTabIndex = computed(() => tabs.findIndex((option) => option.id === tab.value));

const videoLinks = computed(() =>
  personalInfo.socialLinks.filter((link) => link.icon === 'x' || link.icon === 'instagram')
);

useHead({
  title: `Explore - ${personalInfo.fullName}`,
  meta: [
    {
      name: 'description',
      content: `Generated imagery and video by ${personalInfo.fullName}.`
    },
    { property: 'og:title', content: `Explore - ${personalInfo.fullName}` },
    { property: 'og:description', content: `Generated imagery and video by ${personalInfo.fullName}.` },
    { name: 'twitter:title', content: `Explore - ${personalInfo.fullName}` },
    { name: 'twitter:description', content: `Generated imagery and video by ${personalInfo.fullName}.` },
  ]
});

// Locked to the screen on a phone.
//
// Below the breakpoint the horizontal track is torn down and this becomes an
// ordinary document, which is why it could be dragged at all: the rail fills
// the viewport exactly and is swiped side to side, so a vertical drag has
// nothing to reveal and on iOS only ever showed the page rubber-banding under
// it.
//
// A class on the root rather than styles here, because what has to be closed is
// the document's own scrolling and a scoped stylesheet cannot reach html or
// body. The rule lives in app.vue beside the other root-level ones. It closes
// the document and nothing else: the rail keeps its own horizontal scrolling.
const LOCK = 'is-viewport-locked';
let lockQuery = null;

function applyLock() {
  document.documentElement.classList.toggle(LOCK, !!lockQuery?.matches);
}

function releaseLock() {
  document.documentElement.classList.remove(LOCK);
}

onMounted(() => {
  if (route.query.tab && window.location.search) {
    window.history.replaceState(window.history.state, '', window.location.pathname);
  }

  lockQuery = window.matchMedia('(max-width: 768px)');
  lockQuery.addEventListener('change', applyLock);
  applyLock();

  nextTick(() => {
    start(wrapper, track);
  });
});

// Before, not after, unmount — see the note in pages/index.vue: GSAP can only
// unwind its injected pin-spacer while the nodes are still in the DOM.
onBeforeUnmount(() => {
  lockQuery?.removeEventListener('change', applyLock);
  // Taken off on the way out, or every page after this one is locked too.
  releaseLock();
  stop();
});
</script>

<style lang="scss" scoped>
.explore {
  position: relative;
  width: 100%;
  height: 100%;
}

.tabs {
  position: fixed;
  z-index: 1900;
  top: clamp(1.15rem, 3vh, 1.9rem);
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  gap: 0.3rem;
  padding: 0.25rem;
  border-radius: 999px;
  background: rgba(10, 14, 20, 0.86);
  border: 1px solid rgba(255, 255, 255, 0.14);
}

.tabs__glider {
  position: absolute;
  top: 0.25rem;
  bottom: 0.25rem;
  left: 0.25rem;
  width: calc((100% - 0.5rem - 0.3rem) / 2);
  border-radius: 999px;
  background: var(--accent);
  transform: translateX(calc(var(--active, 0) * (100% + 0.3rem)));
  transition: transform 0.32s cubic-bezier(0.4, 0, 0.2, 1);
}

.tabs__pill {
  appearance: none;
  border: none;
  cursor: pointer;
  position: relative;
  z-index: 1;
  flex: 1 1 0;
  white-space: nowrap;
  padding: 0.42rem 1.05rem;
  border-radius: 999px;
  background: transparent;
  color: rgba(255, 255, 255, 0.66);
  font-family: 'Orbitron', sans-serif;
  font-size: 0.68rem;
  letter-spacing: 1.2px;
  text-transform: uppercase;
  transition: color 0.2s ease;

  &:hover {
    color: #fff;
  }

  &.is-active {
    color: #fff;
  }

  @media (max-width: 768px) {
    padding: 0.38rem 0.85rem;
    font-size: 0.62rem;
  }
}

.eyebrow__all {
  display: inline-flex;
  align-items: center;
  gap: 0.3rem;
  padding: 0.25rem 0.6rem;
  border-radius: 999px;
  border: 1px solid rgba(255, 255, 255, 0.2);
  letter-spacing: 0.12em;
  color: rgba(255, 255, 255, 0.75);
  text-decoration: none;
  pointer-events: auto;
  transition: color 0.2s ease-in-out, border-color 0.2s ease-in-out;

  .eyebrow__arrow { transition: transform 0.2s ease-in-out; }

  &:hover,
  &:focus-visible {
    color: #fff;
    border-color: rgba(255, 255, 255, 0.5);

    .eyebrow__arrow { transform: translateX(2px); }
  }

  // Down to the mark alone, and given the tabs' own backing so it reads as a
  // control against a photograph rather than an outline drawn on one.
  @media (max-width: 768px) {
    gap: 0;
    padding: 0.42rem;
    background: rgba(10, 14, 20, 0.86);
    border-color: rgba(255, 255, 255, 0.14);
  }
}

.eyebrow__mark {
  flex-shrink: 0;
  display: none;

  @media (max-width: 768px) {
    display: block;
  }
}

.eyebrow__text,
.eyebrow__arrow {
  @media (max-width: 768px) {
    display: none;
  }
}

.eyebrow {
  --eyebrow-left: calc(clamp(1rem, 3vw, 1.75rem) + 6.5rem);

  position: fixed;
  z-index: 1900;
  top: clamp(1.15rem, 3vh, 1.9rem);
  left: var(--eyebrow-left);
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.6rem;
  min-height: 2rem;
  font-family: 'Orbitron', sans-serif;
  font-size: 0.62rem;
  letter-spacing: 0.3em;
  text-transform: uppercase;
  color: rgba(255, 255, 255, 0.55);
  // Not a target itself; each link takes its own clicks back.
  pointer-events: none;

  // A hard stop before the tabs, whatever the words come out to. They are
  // centred, so their left edge is half the viewport less half their width;
  // 6.5rem covers that and a margin. Past it the links wrap rather than run
  // underneath, which is what hid the second one on a medium laptop.
  max-width: calc(50vw - var(--eyebrow-left) - 6.5rem);

  @media (max-width: 768px) {
    top: calc(clamp(1.15rem, 3vh, 1.9rem) + 2.75rem);
    left: clamp(1rem, 3vw, 1.75rem);
    max-width: none;
    gap: 0.5rem;
    min-height: 0;
  }
}

@media (prefers-reduced-motion: reduce) {
  .tabs__glider { transition: none; }
}

.empty {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  padding: clamp(1.5rem, 5vw, 3rem);
  background: #0b0f14;

  // The height comes from the screen once there is no pinned ancestor to take
  // it from: the panel is `auto` below this and `100%` resolved against
  // nothing, so the box shrank to its own text and left it under the fixed
  // tabs with nothing to centre it against. svh, never dvh, as in the gallery.
  //
  // The top padding is the room the tabs need. Centring inside what is left
  // puts the text under them rather than behind them.
  @media (max-width: 768px) {
    height: 100vh;
    height: 100svh;
    padding-top: 6.5rem;
    // The page is locked at this width, so anything that does not fit here has
    // nowhere else to go. This is the only way out on a short screen, and it
    // stays inert while the note fits, which it does at every ordinary size.
    overflow-y: auto;
    overscroll-behavior: contain;
  }
}

.empty__inner {
  max-width: 34rem;
  text-align: center;
}

.empty__title {
  font-family: 'Orbitron', sans-serif;
  font-size: clamp(1.15rem, 2.6vw, 1.6rem);
  color: #fff;
  margin-bottom: 0.6rem;
}

.empty__note {
  color: rgba(255, 255, 255, 0.62);
  line-height: 1.7;
  margin-bottom: 1.6rem;
}

.empty__links {
  display: flex;
  justify-content: center;
  flex-wrap: wrap;
  gap: 0.7rem;
}

.empty__link {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.6rem 1.15rem;
  border-radius: 999px;
  border: 1px solid rgba(255, 255, 255, 0.2);
  color: #fff;
  text-decoration: none;
  font-size: 0.85rem;
  transition: border-color 0.2s ease, background 0.2s ease;

  &:hover {
    text-decoration: none;
    border-color: rgba(var(--accent-rgb), 0.7);
    background: rgba(var(--accent-rgb), 0.14);
  }
}
</style>
